import { useCallback, useMemo, useState } from "react";
import qs from "qs";
import { useTranslation } from "react-i18next";
import { generatePath, useNavigate } from "react-router";
import AddCircleOutlinedIcon from "@mui/icons-material/AddCircleOutlined";
import { GridRowParams } from "@mui/x-data-grid-pro";

import { PHOTOS_ACCESSOR, VEHICLE_ACCESSOR } from "shared/schemas/constants";
import { USE_RESOURCE_SCHEMA_MAP } from "shared/schemas/schemaMap";
import { EventTypeEnum } from "shared/types";

import {
  ADDITIONAL_LOAD_COUNT,
  ChartDateFieldEnum,
  CountTypeEnum,
} from "pages/Dashboard/components/constants";
import EventsTableToolbar from "pages/Dashboard/components/EventsTableToolbar";
import TableFilterButton from "pages/Dashboard/components/EventTables/TableFilterButton";
import { getPeriodAttributeValueFromFilter } from "pages/Dashboard/components/utils";
import { MainTableComponentProps } from "pages/Dashboard/types";
import {
  INVESTIGATOR_EVENT_FILTER_KEY,
  INVESTIGATOR_NAVIGATED_FROM_KEY,
  INVESTIGATOR_VEHICLE_FILTER_KEY,
  INVESTIGATOR_VIEW_TITLE_KEY,
} from "pages/Investigator/constants";
import { getDateDropdownOptionText } from "pages/shared/Dashboard/DateFieldDropdown/utils";

import Button from "features/components/Button";
import Tooltip from "features/components/Tooltip";
import {
  getFiltersQuery,
  mergeFilterGroupStates,
} from "features/ui/Filters/FilterBuilder/utils";
import SmartMaterialTable from "features/ui/MaterialTable/SmartMaterialTable";

import { routes } from "services/routes";

import {
  buildSchemaFromColumns,
  createFormatCell,
  createRenderHeader,
  DEFAULT_COLUMN_ORDER,
  getOrderedColumnDefinitions,
  SmartGroupColumnContext,
  SmartGroupColumnId,
} from "./columnDefinitions/utils";
import FilterTooltipContent from "./FilterTooltipContent";
import SmartGroupDetailPanel from "./SmartGroupDetailPanel";
import SmartGroupsTableColumnMenu from "./SmartGroupsTableColumnMenu";
import { getAccessors, getCombinedEventFilters, getLabel } from "./tableUtils";
import { ChartTimeGrouping, SmartGroupData } from "./types";

interface SmartGroupsTableProps extends MainTableComponentProps {
  data: SmartGroupData[];
  loading: boolean;
  eventType: EventTypeEnum;
  countType: CountTypeEnum;
  hasMore: boolean;
  onShowMore: () => void;
  isLoadingMore: boolean;
  eventLabels: { label: string; chartKey: string };
  rightContent?: React.ReactNode;
  chartTimeGrouping: ChartTimeGrouping;
  onChartTimeGroupingChange: (value: ChartTimeGrouping) => void;
}

const SmartGroupsTable = ({
  eventFilters,
  vehiclesFilters,
  allTimeVehiclesFilters,
  onBadRequest,
  config,
  data,
  loading,
  eventType,
  countType,
  smartGroups,
  onSmartGroupsChange,
  dashboardName,
  groupedCount,
  ungroupedCount,
  showUngroupedEvents,
  setShowUngroupedEvents,
  hasMore,
  onShowMore,
  isLoadingMore,
  eventLabels,
  rightContent,
  sharedTableFilterState,
  dateFieldOption,
  chartTimeGrouping,
  onChartTimeGroupingChange,
  advancedFiltersDatePeriod,
  // `stickyFooter` is deliberately not destructured: SmartMaterialTable hides
  // its own footer and the nested detail-panel MaterialTable intentionally
  // keeps the footer at its natural bottom position (see SmartGroupDetailPanel).
  stickyHeader,
}: SmartGroupsTableProps) => {
  const { t } = useTranslation();
  const [showOnlyEventDateDataState, setShowOnlyEventDateDataState] =
    useState(false);
  const isSimplified = config.simplified ?? false;
  const showOnlyEventDateData = isSimplified || showOnlyEventDateDataState;
  const [hoveredRowId, setHoveredRowId] = useState<string | null>(null);
  const [filterTooltipOpen, setFilterTooltipOpen] = useState(false);
  const navigate = useNavigate();

  // schema for filter display
  const { schema: filterSchema } = USE_RESOURCE_SCHEMA_MAP[eventType]([
    VEHICLE_ACCESSOR,
    PHOTOS_ACCESSOR,
  ]);

  const activeFilterCount = sharedTableFilterState?.filters
    ? Object.keys(sharedTableFilterState.filters.children || {}).length
    : 0;
  const hasActiveFilters = activeFilterCount > 0;

  const handleRemoveAllFilters = useCallback(() => {
    if (sharedTableFilterState?.resetFilters) {
      sharedTableFilterState.resetFilters();
      setFilterTooltipOpen(false);
    }
  }, [sharedTableFilterState]);

  const { label: eventLabel, chartKey } = eventLabels;
  const { countLabel, chartLabel } = getLabel(countType, eventLabel, t);
  const { filteredAccessor, allTimeAccessor } = getAccessors(countType);

  const eventsFilterPeriodText = dateFieldOption
    ? getDateDropdownOptionText(dateFieldOption, t).toLowerCase()
    : t("schema:dashboard.selected_period");

  const eventsFilterPeriod = config.treatEventDateAsVehicleManufacturedAt
    ? t("schema:dashboard.period_of_prod", { period: eventsFilterPeriodText })
    : eventsFilterPeriodText;

  const eventsPeriod =
    config.useDateFromAdvancedFilter && advancedFiltersDatePeriod
      ? getDateDropdownOptionText(advancedFiltersDatePeriod, t).toLowerCase()
      : eventsFilterPeriod;

  const { period: vehiclesPeriod, addProductionSuffix } =
    getPeriodAttributeValueFromFilter(
      [
        { attr: "vehicleManufacturedAt", addProductionSuffix: true },
        {
          attr: config.advancedFilterDateParentAttribute ?? "lineExits",
          addProductionSuffix: false,
          dateChildAttribute:
            config.advancedFilterDateChildAttribute ?? "exitTime",
        },
      ],
      config.useDateFromAdvancedFilter && allTimeVehiclesFilters
        ? allTimeVehiclesFilters
        : vehiclesFilters,
      t
    );

  const isEventDate =
    config.chartSettings.chartDateField === ChartDateFieldEnum.EVENT_DATE;

  // calculate max values for each column once for the bar width calculations
  // for FRC Impact, use absolute values since they are negative
  const isFRCImpact = countType === CountTypeEnum.FRC_IMPACT;
  const maxFilteredValue = Math.max(
    ...data.map((row) =>
      isFRCImpact
        ? Math.abs(row[filteredAccessor] || 0)
        : row[filteredAccessor] || 0
    )
  );
  const maxAllTimeValue = Math.max(
    ...data.map((row) =>
      isFRCImpact
        ? Math.abs(row[allTimeAccessor] || 0)
        : row[allTimeAccessor] || 0
    )
  );

  const handleGetCombinedEventFilters = useCallback(
    (smartGroupID: string) =>
      getCombinedEventFilters(smartGroupID, eventFilters),
    [eventFilters]
  );

  const onOpenDetails = useCallback(
    (id: string) => {
      const tableFilters = sharedTableFilterState?.filters;
      const mergedEventFilters = mergeFilterGroupStates(
        eventFilters,
        tableFilters
      );

      const search = qs.stringify({
        [INVESTIGATOR_EVENT_FILTER_KEY]: getFiltersQuery(mergedEventFilters),
        [INVESTIGATOR_VEHICLE_FILTER_KEY]: getFiltersQuery(vehiclesFilters),
        [INVESTIGATOR_VIEW_TITLE_KEY]: config.title,
        [INVESTIGATOR_NAVIGATED_FROM_KEY]: "dashboard",
      });

      navigate({
        pathname: generatePath(routes.smartGroup, { id }),
        search,
      });
    },
    [
      sharedTableFilterState?.filters,
      eventFilters,
      vehiclesFilters,
      config.title,
      navigate,
    ]
  );

  const columnContext: SmartGroupColumnContext = useMemo(
    () => ({
      countType,
      countLabel,
      chartLabel,
      chartKey,
      eventType,
      filteredAccessor,
      allTimeAccessor,
      eventsPeriod,
      vehiclesPeriod,
      addProductionSuffix,
      isEventDate,
      showOnlyEventDateData,
      onToggleShowOnlyEventDateData: () =>
        setShowOnlyEventDateDataState((prev) => !prev),
      isSimplified,
      chartTimeGrouping,
      maxFilteredValue,
      maxAllTimeValue,
      hoveredRowId,
      onOpenDetails,
      t,
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      countType,
      countLabel,
      chartLabel,
      chartKey,
      eventType,
      filteredAccessor,
      allTimeAccessor,
      eventsPeriod,
      vehiclesPeriod,
      addProductionSuffix,
      isEventDate,
      showOnlyEventDateData,
      isSimplified,
      chartTimeGrouping,
      maxFilteredValue,
      maxAllTimeValue,
      hoveredRowId,
      onOpenDetails,
    ]
  );

  const columnOrder = config.smartGroupsTableColumns ?? DEFAULT_COLUMN_ORDER;
  const columnDefinitions = useMemo(
    () => getOrderedColumnDefinitions(columnOrder),
    [columnOrder]
  );

  const schema = useMemo(
    () => buildSchemaFromColumns(columnDefinitions, columnContext),
    [columnDefinitions, columnContext]
  );

  const renderHeader = useMemo(
    () => createRenderHeader(columnDefinitions, columnContext),
    [columnDefinitions, columnContext]
  );

  const formatCell = useMemo(
    () => createFormatCell(columnDefinitions, columnContext),
    [columnDefinitions, columnContext]
  );

  const renderPanel = useCallback(
    (params: GridRowParams) => (
      <SmartGroupDetailPanel
        row={params.row}
        eventType={eventType}
        countType={countType}
        showOnlyEventDateData={showOnlyEventDateData}
        chartLabel={chartLabel}
        eventsPeriod={eventsPeriod}
        getCombinedEventFilters={handleGetCombinedEventFilters}
        vehiclesFilters={vehiclesFilters}
        onBadRequest={onBadRequest}
        config={config}
        sharedTableFilterState={sharedTableFilterState}
        chartTimeGrouping={chartTimeGrouping}
        stickyHeader={stickyHeader}
      />
    ),
    [
      eventType,
      countType,
      showOnlyEventDateData,
      chartLabel,
      eventsPeriod,
      handleGetCombinedEventFilters,
      vehiclesFilters,
      onBadRequest,
      config,
      sharedTableFilterState,
      chartTimeGrouping,
      stickyHeader,
    ]
  );

  const toolbar = () => (
    <EventsTableToolbar
      smartGroups={smartGroups}
      onSmartGroupsChange={onSmartGroupsChange}
      smartGroupsAllowed={config.enableSmartGroups ?? false}
      dashboardName={dashboardName}
      groupedCount={groupedCount}
      ungroupedCount={ungroupedCount}
      showUngroupedEvents={showUngroupedEvents}
      setShowUngroupedEvents={setShowUngroupedEvents}
      isLoading={loading}
      rightContent={
        <div className="flex items-center gap-2">
          <Tooltip
            title={
              <FilterTooltipContent
                hasActiveFilters={hasActiveFilters}
                filters={sharedTableFilterState?.filters}
                filterSchema={filterSchema}
                onRemoveAllFilters={handleRemoveAllFilters}
                onClose={() => setFilterTooltipOpen(false)}
              />
            }
            placement="bottom-end"
            enterDelay={300}
            leaveDelay={0}
            open={filterTooltipOpen}
            onOpen={() => setFilterTooltipOpen(true)}
            onClose={() => setFilterTooltipOpen(false)}
          >
            <span>
              <TableFilterButton
                activeFilterCount={activeFilterCount}
                disabled={!sharedTableFilterState}
                dataTestId="smart-groups-table-filter-button"
              />
            </span>
          </Tooltip>
          {rightContent}
        </div>
      }
    />
  );

  return (
    <div>
      <SmartMaterialTable
        testId="smart-groups-table"
        schema={schema}
        data={data}
        getRowId={(row) => row.groupName}
        formatCell={formatCell}
        loading={loading}
        renderHeader={renderHeader}
        renderPanel={renderPanel}
        toolbar={toolbar}
        disableColumnMenu={false}
        onRowHover={setHoveredRowId}
        pageKey={`smart-groups-${dashboardName}-${eventType}`}
        columnMenu={SmartGroupsTableColumnMenu}
        columnMenuProps={{
          chartKey,
          chartLabel,
          chartTimeGrouping,
          onChartTimeGroupingChange,
        }}
        stickyHeader={stickyHeader}
        pinnedRightColumns={[SmartGroupColumnId.ACTIONS]}
      />
      {hasMore && !loading && (
        <div className="flex justify-center mt-4 mb-4">
          <Button
            color="secondary"
            onClick={onShowMore}
            loading={isLoadingMore}
            label={t("schema:dashboard.show_more_items", {
              count: Math.min(
                ADDITIONAL_LOAD_COUNT,
                (groupedCount ?? Infinity) - data.length
              ),
            })}
            endIcon={<AddCircleOutlinedIcon sx={{ fontSize: 20 }} />}
          />
        </div>
      )}
    </div>
  );
};

export default SmartGroupsTable;
